<template>
  <!-- Mobile Caption Settings: covers the top bar with a row of categories; the picked category
       opens its controls in a second row underneath. Stays open until Close. -->
  <div class="caption-settings-bar topBar absolute left-0 top-0 z-[110] w-full flex flex-col bg-layer-2" data-testid="caption-settings-bar">
    <div class="caption-settings-bar__row flex items-center gap-2 h-[82px] px-4 py-[10px]">
      <button type="button" class="top-bar-button flex-none min-w-[64px]" data-testid="caption-settings-close" @click="emit('close')">
        <XMarkIcon class="w-6 h-6" aria-hidden="true" />
        <span>{{ closeLabel }}</span>
      </button>
      <button
          v-for="category in categories"
          :key="category.value"
          type="button"
          class="top-bar-button flex-none min-w-[64px]"
          :class="{ pressed: activeCategory === category.value }"
          :aria-pressed="activeCategory === category.value"
          :data-testid="`caption-settings-category-${category.value}`"
          @click="toggleCategory(category.value)"
      >
        <img class="bar-icon w-6 h-6 object-contain" :src="category.icon" alt="" />
        <span>{{ category.label }}</span>
      </button>
    </div>

    <div
        v-if="activeCategory"
        class="caption-settings-bar__row caption-settings-bar__options flex items-center gap-3 h-[62px] px-4 bg-layer-1 whitespace-nowrap"
        :data-testid="`caption-settings-options-${activeCategory}`"
    >
      <span class="caption-settings-bar__label shrink-0 text-[14px] leading-[20px] font-medium text-text-neutral-high">{{ activeCategoryLabel }}:</span>

      <template v-if="activeCategory === 'size'">
        <div class="flex flex-row shrink-0 rounded-[10px] overflow-hidden">
          <button :disabled="fontSize <= minFontSize" class="caption-settings-bar__step bg-[#282F35] h-10 w-10 flex justify-center items-center cursor-pointer disabled:opacity-50 disabled:cursor-default"
            data-testid="font-decrease" @click="emit('step-font-size', -1)" aria-label="Decrease Font Size">
            <img class="bar-icon" width="24" height="24" src="@/assets/icons/minus.svg" alt="-" />
          </button>
          <button :disabled="fontSize >= maxFontSize" class="caption-settings-bar__step bg-[#282F35] h-10 w-10 flex justify-center items-center border-l-[3px] border-layer-1 cursor-pointer disabled:opacity-50 disabled:cursor-default"
            data-testid="font-increase" @click="emit('step-font-size', 1)" aria-label="Increase Font Size">
            <img class="bar-icon" width="24" height="24" src="@/assets/icons/plus.svg" alt="+" />
          </button>
        </div>
        <input
            v-model="fontSizeDraft"
            type="number"
            inputmode="numeric"
            :min="minFontSize"
            :max="maxFontSize"
            step="1"
            :aria-label="sizeLabel"
            class="caption-settings-bar__input font-size-input shrink-0 border-2 border-[#292F36] rounded-[10px] bg-transparent text-text-neutral-extreme font-normal text-[16px] h-10 w-[64px] px-[10px] outline-none focus:border-[#6CBFFF]"
            data-testid="font-size-input"
            @change="commitFontSize"
            @keydown.enter="$event.target.blur()"
        />
      </template>

      <div v-else class="flex flex-row gap-2" role="radiogroup" :aria-label="activeCategoryLabel">
        <button
            v-for="option in activeOptions"
            :key="option.value"
            type="button"
            role="radio"
            :aria-checked="activeValue === option.value"
            class="caption-settings-bar__chip shrink-0 h-10 px-4 rounded-[10px] text-[16px] leading-[20px] text-text-neutral-high cursor-pointer"
            :class="activeValue === option.value ? 'caption-settings-bar__chip--active bg-button-secondary-mono-active' : 'bg-button-secondary-mono-resting'"
            :style="option.css ? { fontFamily: option.css } : null"
            :data-testid="`${activeCategory}-option-${option.value}`"
            @click="emit(activeCategory, option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { translate, useCaptionSettingsOptions, useFontSizeDraft } from '@/composables/useCaptionSettings';
import fontSizeIcon from '@/assets/icons/sorenson/font-size.svg';
import fontIcon from '@/assets/icons/sorenson/font-family.svg';
import themeIcon from '@/assets/icons/sorenson/theme.svg';

const props = defineProps({
  fontSize: {
    type: Number,
    required: true,
  },
  minFontSize: Number,
  maxFontSize: Number,
  align: {
    type: String,
    required: false,
    default: 'left',
  },
  // Style key from Constants.CAPTION_FONTS, not a font name.
  font: {
    type: String,
    required: false,
    default: 'sans',
  },
  theme: {
    type: String,
    required: false,
    default: 'dark',
  },
})

// Category events ('align', 'font', 'theme') carry the picked option, same as CaptionSettings.
const emit = defineEmits(['close', 'step-font-size', 'set-font-size', 'align', 'font', 'theme'])

const {
  sizeLabel,
  alignmentLabel,
  fontLabel,
  themeLabel,
  alignOptions,
  fontOptions,
  themeOptions,
} = useCaptionSettingsOptions()

const { fontSizeDraft, commitFontSize } = useFontSizeDraft(props, emit)

const closeLabel = computed(() => translate('common_close', 'Close'))

// Width is left out on purpose: its narrow column is wider than a phone screen anyway.
const categories = computed(() => [
  { value: 'size', label: translate('caption_settings_category_size', 'Size'), icon: fontSizeIcon },
  // shows the alignment that is currently picked
  { value: 'align', label: alignmentLabel.value, icon: (alignOptions.value.find((option) => option.value === props.align) || alignOptions.value[0]).icon },
  { value: 'font', label: fontLabel.value, icon: fontIcon },
  { value: 'theme', label: themeLabel.value, icon: themeIcon },
])

// Nothing is open until a category is tapped; tapping the open one folds it again.
const activeCategory = ref(null)

const toggleCategory = (category) => {
  activeCategory.value = activeCategory.value === category ? null : category
}

const activeCategoryLabel = computed(() => ({
  size: sizeLabel,
  align: alignmentLabel.value,
  font: fontLabel.value,
  theme: themeLabel.value,
})[activeCategory.value])

const activeOptions = computed(() => ({
  align: alignOptions.value,
  font: fontOptions.value,
  theme: themeOptions.value,
})[activeCategory.value] || [])

const activeValue = computed(() => ({
  align: props.align,
  font: props.font,
  theme: props.theme,
})[activeCategory.value])
</script>

<style scoped>
/* Both rows scroll sideways on narrow phones, without a visible scrollbar. */
.caption-settings-bar__row {
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.caption-settings-bar__row::-webkit-scrollbar {
  display: none;
}

.font-size-input {
  -moz-appearance: textfield;
  appearance: textfield;
  text-align: center;
}

.font-size-input::-webkit-outer-spin-button,
.font-size-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
